import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ConfigurationModel, FeatureFlagEnum} from '../../models/configuration.model';
import {Asset} from '../../../pages/live-map/models/asset.class';
import {Observable, Subscription} from 'rxjs';
import {FormControl, ReactiveFormsModule } from '@angular/forms';
import {ShiftState} from '../../models/shift.model';
import {AssetsManagerService} from '../../../data/assets/assets-manager.service';
import {map, startWith} from 'rxjs/operators';
import {AddressSuggestion, ArcgisApiService} from '../../../data/address-search/arcgis-api.service';
import {AddressLookupMapMarkerService} from '../map-viewer/services/address-lookup-map-marker.service';
import {Router} from '@angular/router';
import {RouteConfigurationWithSchema, RouteHierarchyItem, RouteHierarchyItemWithPath} from '../../models/route';
import {Poi} from '../../models/poi.model';
import {PoiService} from '../../../data/address-search/poi.service';
import {LiveMapDataService} from '../../../pages/live-map/services/live-map-data.service';
import { AssetStatus } from '../../models/asset-status';
import { MatAutocomplete, MatAutocompleteOrigin, MatAutocompleteTrigger, MatOptgroup, MatOption } from '@angular/material/autocomplete';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';

export class SearchResult {
  constructor(
      public type: SearchResultType,
      public result: Asset | AddressSuggestion | RouteHierarchyItem | Poi,
  ) {
  }
}

export enum SearchResultType {
  ADDRESS = 'address',
  VEHICLE = 'vehicle',
  ROUTE = 'route',
  POI = 'poi',
}

@Component({
  selector: 'app-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  imports: [
    MatAutocompleteOrigin,
    NgClass,
    MatIcon,
    MatInput,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatIconButton,
    MatAutocomplete,
    MatOptgroup,
    MatOption,
    MatTooltip,
    AsyncPipe,
  ],
})
export class SearchBarComponent implements OnInit, OnDestroy {
  @Input() configuration: ConfigurationModel;
  @Input() isCompact: boolean;
  @Output() searchResulted = new EventEmitter<SearchResult>();

  private assets: Asset[] = [];
  filteredAssets$: Observable<Asset[]>;
  addressSuggestions: AddressSuggestion[];
  private routeItems: RouteHierarchyItemWithPath[] = []; // routes (only leaves)
  routeSuggestions: RouteHierarchyItemWithPath[];
  pois: Poi[] = [];

  @ViewChild('searchInput') private searchInputElement: ElementRef<HTMLElement>;
  searchControl = new FormControl<string | SearchResult>('');
  showAutocomplete = false;

  FeatureFlagEnum = FeatureFlagEnum;
  ShiftStatus = ShiftState;
  SearchResultType = SearchResultType;
  AssetStatus = AssetStatus;
  private readonly openSubscriptions = Array<Subscription>();

  constructor(
    private router: Router,
    private assetsManager: AssetsManagerService,
    private liveMapDataService: LiveMapDataService,
    private arcGisApiService: ArcgisApiService,
    private poiService: PoiService,
    private addressLookupMapMarkerService: AddressLookupMapMarkerService
  ) {}

  ngOnInit() {
    // do not serialize Date type, we do not want to search by dates
    Date.prototype.toJSON = () => {
      return '';
    };

    const assetSubscription = this.assetsManager.assets$.subscribe(assets => {
      this.assets = assets;
    });
    this.openSubscriptions.push(assetSubscription);

    this.filteredAssets$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        return this.filterSearchQuery(value);
      })
    );

    const routeSubscription = this.liveMapDataService.routeConfiguration$.subscribe(routeConfiguration => {
      this.routeItems = routeConfiguration
        .map(routeConfig => {
          return this.routeConfigToHierarchy(routeConfig);
        })
        .flat();
    });
    this.openSubscriptions.push(routeSubscription);

    this.searchControl.valueChanges.subscribe(value => {
      this.showDropdown(value);
    });
  }

  ngOnDestroy() {
    this.openSubscriptions.forEach(subscription => subscription?.unsubscribe());
  }

  hasFeatureFlag(featureFlag: string): boolean {
    return this.configuration.featureFlags.find(value => value.isEnabled && value.name === featureFlag) !== undefined;
  }

  onFocus(e) {
    this.showDropdown(this.searchControl.value);
  }

  showDropdown(e: any) {
    e && e.length >= 1 ? (this.showAutocomplete = true) : (this.showAutocomplete = false);
  }

  onFocusOut(e) {
    this.showAutocomplete = false;
  }

  filterSearchQuery(value: string | SearchResult): Asset[] {
    if (!value || value === '') {
      return [];
    }
    if (typeof value === 'string') {
      // find poi
      if (this.hasFeatureFlag(FeatureFlagEnum.Poi)) {
        this.poiAutocomplete(value);
      }

      // find route
      this.routeAutocomplete(value);

      // find addresses/places
      this.addressAutocomplete(value);

      // filter assets
      return this.assets.filter(asset => {
        let imeisv = '';
        if (asset.vehicleLmuId?.length === 18) {
          // IMEI found, add IMEISV to search target
          imeisv = asset.vehicleLmuId.replace(/-/g, '').substring(0, 14) + 32;
        }
        const searchTarget = Object.values(asset)
          .map(v => (v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v)))
          .join(' ')
          .concat(' ', imeisv)
          .toLowerCase();
        return searchTarget.indexOf(String(value).toLowerCase()) !== -1;
      });
    } else {
      return [];
    }
  }

  onPressEnter(e: Event) {
    const searchResult: SearchResult = this.searchControl.value as SearchResult;
    switch (searchResult?.type) {
      case SearchResultType.VEHICLE:
        this.vehicleSelected(searchResult.result as Asset);
        break;
      case SearchResultType.ADDRESS:
        this.addressSelected(searchResult.result as AddressSuggestion);
        break;
      case SearchResultType.ROUTE:
        this.routeSelected(searchResult.result as RouteHierarchyItemWithPath);
        break;
      case SearchResultType.POI:
        this.poiSelected(searchResult.result as Poi);
        break;
      default:
        console.warn('Missing implementation to get search result on enter press! Probably nothing found.');
        console.log(searchResult);
    }
  }

  onSearchBoxFocus(e: Event) {
    // focus really on input control instead of div
    this.searchInputElement.nativeElement.focus();
  }

  displayFn(searchResult: SearchResult): string {
    if (!searchResult) {
      return '';
    }
    switch (searchResult.type) {
      case SearchResultType.VEHICLE:
        const asset = searchResult.result as Asset;
        return !!asset && !!asset.name ? asset.name : '';
      case SearchResultType.ADDRESS:
        const addressSuggestion = searchResult.result as AddressSuggestion;
        return addressSuggestion.text;
      case SearchResultType.ROUTE:
        const routeItem = searchResult.result as RouteHierarchyItem;
        return routeItem.value;
      case SearchResultType.POI:
        const poi = searchResult.result as Poi;
        return poi.name;
      default:
        console.warn('Missing implementation to display search result!');
        return '';
    }
  }

  clearSearch() {
    this.searchControl.enable();
    this.searchControl.reset('');
    this.searchControl.markAsUntouched();
    if (this.addressLookupMapMarkerService.isInitialized) {
      // no need now as we show result in LiveAddress component
      // this.addressLookupMapMarkerService.clearLocations();
    }
    this.showAutocomplete = false;
    this.addressSuggestions = [];
    this.routeSuggestions = [];
    this.pois = [];
    this.searchInputElement.nativeElement.blur();
    this.addressSuggestions = [];
  }

  private addressAutocomplete(value: string) {
    this.arcGisApiService.suggestAddresses(value).then(suggestions => {
      if (!!this.searchControl.value && this.searchControl.value !== '') {
        this.addressSuggestions = suggestions;
      }
    });
  }

  private poiAutocomplete(value: string) {
    if (value.length > 3) {
      this.poiService
        .searchByName(value)
        .then(response => {
          this.pois = response.data;
        });
    }
  }

  private routeConfigToHierarchy(routeConfiguration: RouteConfigurationWithSchema): RouteHierarchyItemWithPath[] {
    return RouteHierarchyItem.getLeaves(routeConfiguration.schema.classification.length, routeConfiguration.configuration.children).map(
      item => {
        item.configId = routeConfiguration.id;
        item.routeName = null;
        return item;
      }
    );
  }

  private routeAutocomplete(value: string) {
    this.routeSuggestions = this.routeItems.filter(
      route => JSON.stringify(route).toLowerCase().indexOf(String(value).toLowerCase()) !== -1
    );
  }

  getPoiIconName() {
    // the only POIs so far are Wyoming mileposts
    return 'signpost';
  }

  vehicleSelected(asset: Asset) {
    this.searchResulted.emit(new SearchResult(SearchResultType.VEHICLE, asset));
    this.clearSearch();
  }

  addressSelected(addressSuggestion: AddressSuggestion) {
    this.searchResulted.emit(new SearchResult(SearchResultType.ADDRESS, addressSuggestion));
    this.clearSearch();
  }

  routeSelected(routeItem: RouteHierarchyItemWithPath) {
    this.searchResulted.emit(new SearchResult(SearchResultType.ROUTE, routeItem));
    this.clearSearch();
  }

  poiSelected(poi: Poi) {
    this.searchResulted.emit(new SearchResult(SearchResultType.POI, poi));
    this.clearSearch();
  }
}
